import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch
} from '@mui/material';
import { SquarePen } from 'lucide-react';
import { getAllFeatures, createFeature, updateFeature } from '../../../Services/AddRetreatManagement/RetreatDetailsApi';
import PermissionWrapper from '../../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../../Components/Common/GlobalSanckbar';


const GetRetreatFeatures = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [features, setFeatures] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        displayOrder: '1',
        isActive: true
    });
    const [currentItem, setCurrentItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const fetchFeatures = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllFeatures();
            if (response && response.success) {
                setFeatures(response.data);
            } else {
                setError("Failed to load features.");
            }
        } catch (err) {
            setError("Failed to fetch features. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFeatures();
    }, [fetchFeatures]);

    const handleOpenModal = (item = null) => {
        if (item) {
            setIsEdit(true);
            setCurrentItem(item);
            const active = item.isAction !== undefined ? Boolean(item.isAction) : (item.isActive !== undefined ? Boolean(item.isActive) : true);
            setFormData({
                name: item.name,
                displayOrder: item.displayOrder !== undefined && item.displayOrder !== null && item.displayOrder !== '' ? String(item.displayOrder) : '1',
                isActive: active
            });
        } else {
            setIsEdit(false);
            setCurrentItem(null);
            setFormData({
                name: '',
                displayOrder: '1',
                isActive: true
            });
        }
        setValidationErrors({});
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentItem(null);
        setFormData({ name: '', displayOrder: '1', isActive: true });
        setValidationErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        if (name === 'displayOrder') {
            if (value !== "" && (!/^\d+$/.test(value) || Number(value) > 999)) {
                return;
            }
        }
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSubmit = async () => {
        if (!formData.name) {
            setValidationErrors({ name: "Feature name is required" });
            return;
        }

        if (formData.displayOrder !== "" && formData.displayOrder !== null && formData.displayOrder !== undefined && (Number(formData.displayOrder) > 999 || Number(formData.displayOrder) < 1)) {
            setValidationErrors({ displayOrder: "Display order must be between 1 and 999." });
            return;
        }

        setSubmitting(true);
        setError(null);

        const payload = {
            name: formData.name,
            isActive: formData.isActive,
            isAction: formData.isActive,
            ...(formData.displayOrder !== "" && formData.displayOrder !== null && formData.displayOrder !== undefined ? { displayOrder: Number(formData.displayOrder) } : {})
        };

        try {
            if (isEdit) {
                await updateFeature(currentItem.id, payload);
            } else {
                await createFeature(payload);
            }

            handleCloseModal();
            fetchFeatures();
            showSnackbar(`Feature ${isEdit ? 'updated' : 'created'} successfully!`, 'success');
        } catch (err) {
            const serverError = err.response?.data;
            const errorMessage = serverError?.message || `Failed to ${isEdit ? 'update' : 'create'} feature`;
            if (serverError && serverError.details) {
                const fieldErrors = {};
                serverError.details.forEach(detail => {
                    const fieldName = detail.field === 'general' ? 'name' : detail.field;
                    fieldErrors[fieldName] = detail.message;
                });
                setValidationErrors(fieldErrors);
            } else {
                setValidationErrors({ name: errorMessage });
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Retreat Features
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 950 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Feature Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Display Order</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px', textAlign: 'right' }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                            <CircularProgress sx={{ color: '#FFBD65' }} size={24} />
                                        </TableCell>
                                    </TableRow>
                                ) : features.length > 0 ? (
                                    features.map((item, idx) => {
                                        const active = item.isAction !== undefined ? Boolean(item.isAction) : (item.isActive !== undefined ? Boolean(item.isActive) : false);
                                        return (
                                            <TableRow key={item.id} sx={{
                                                transition: 'background-color 0.2s',
                                                '& td': { borderBottom: '1px solid #F0F0F0' },
                                                '&:last-child td': { borderBottom: 0 }
                                            }}>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>{item.name}</TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666' }}>{item.displayOrder !== undefined && item.displayOrder !== null ? item.displayOrder : 1}</TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                    <Box sx={{
                                                        display: 'inline-block',
                                                        px: 1.5, py: 0.3,
                                                        borderRadius: '4px',
                                                        bgcolor: active ? '#E8F5E9' : '#FEECEB',
                                                        color: active ? '#2E7D32' : '#F44336',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600
                                                    }}>
                                                        {active ? "Active" : "Inactive"}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                    {item.cuName || '-'}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                    {item.uuName || '-'}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                    {formatDate(item.createdAt)}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                    {formatDate(item.updatedAt)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                        <PermissionWrapper permission="edit" mode="disable">
                                                            <Tooltip title="Edit">
                                                                <IconButton disableFocusRipple disableRipple disableTouchRipple size="small" sx={{ color: '#FFBD65' }} onClick={() => handleOpenModal(item)}>
                                                                    <SquarePen size={16} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </PermissionWrapper>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No features found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        {isEdit ? 'Edit Feature' : 'Add Feature'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                label="Feature Name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="e.g. Yoga Mats provided"
                                error={!!validationErrors.name}
                                helperText={validationErrors.name}
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                            <TextField
                                fullWidth
                                type="number"
                                label="Display Order"
                                name="displayOrder"
                                value={formData.displayOrder}
                                onChange={handleInputChange}
                                placeholder="Enter display order (1 - 999)"
                                inputProps={{ min: 1, max: 999, onWheel: (e) => e.target.blur() }}
                                error={Boolean(validationErrors.displayOrder)}
                                helperText={validationErrors.displayOrder}
                                size="small"
                                sx={{
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                    '& input[type=number]': {
                                        MozAppearance: 'textfield',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleInputChange}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFBD65' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFBD65' }
                                        }}
                                    />
                                }
                                label={<span style={{ fontSize: '0.85rem' }}>Active Status</span>}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button onClick={handleCloseModal} sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem', px: 2.5 }}>Cancel</Button>
                        <Button
                            disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleSubmit}
                            disabled={submitting}
                            variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                color: '#fff',
                                textTransform: 'none',
                                borderRadius: '6px',
                                px: 3,
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Add')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </PermissionWrapper>
    );
};

export default GetRetreatFeatures;